import React, { useEffect, useState } from 'react';
import {
	Box,
	Paper,
	Typography,
	Button,
	Chip,
	Avatar,
	Tabs,
	Tab,
	Grid,
	Alert,
	Stack,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Card,
	CardContent,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Skeleton,
	IconButton,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SyncIcon from '@mui/icons-material/Sync';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import DevicesIcon from '@mui/icons-material/Devices';
import FolderIcon from '@mui/icons-material/Folder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TimelineIcon from '@mui/icons-material/Timeline';
import MainGrid from '../../components/MainGrid';
import { toast } from 'react-toastify';
import {
	deleteResident,
	fetchCarePlanById,
	fetchElder,
	fetchElderCareNotes,
	fetchElderDataFile,
	fetchElderFiles,
	fetchExternalElder,
	fetchExternalElderPlan,
	fetchExternalElderReview,
	fetchExternalLatestElderPlanData,
	fetchLatestElderPlanData,
} from '../../services/ElderService';
import { getAccessToken, getElderHomeId } from '../../Utils/AuthUtil';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AutoAwesome, Check, Delete, Description } from '@mui/icons-material';
import { dateConverter } from '../../Utils/Helpers';
import { DialogTitleStyle } from '../../assets/theme/Theme';
import { border, dark, surface, teal } from '../../assets/theme/Colors';

const FILE_ITEM_FILTER = [
	'All',
	'Onboarding',
	'Initial Care Plan',
	'Optimized Care Plan',
];

const colors = {
	tealLight: '#e6f9f7',
	tealBorder: '#b3ede8',
	bg: '#f0f5f5',
	muted: '#6b8888',
	text: '#1a2424',
	danger: '#8a2020',
	dangerBg: '#fde8e8',
	warning: '#8a5c00',
	warningBg: '#fef5e6',
	infoBg: '#e6f1fb',
};

const tagStyles = {
	blue: {
		bgcolor: '#e6f1fb',
		color: '#1a508a',
		border: '1px solid #b5d4f4',
	},
	red: {
		bgcolor: '#fde8e8',
		color: '#8a2020',
		border: '1px solid #f09595',
	},
	amber: {
		bgcolor: '#fef3e2',
		color: '#7c5200',
		border: '1px solid #fac775',
	},
	purple: {
		bgcolor: '#eeedfe',
		color: '#3c3489',
		border: '1px solid #afa9ec',
	},
	gray: {
		bgcolor: surface,
		color: '#6b8888',
		border: '1px solid #d8e8e8',
	},
	teal: {
		bgcolor: '#e6f9f7',
		color: '#0a5050',
		border: '1px solid #b3ede8',
	},
};

const sectionIconBg = {
	backgroundColor: '#ecfeff',
	color: '#0f766e',
};

const timelineData = [
	{
		title: 'Fall risk escalated — AI detected pattern from Arquella monitoring data',
		meta: 'Today · Automatic detection',
		body: '40% increase in night movement over 7 days. Two near-fall events in care notes. Care plan review flagged as urgent.',
		color: colors.danger,
	},
	{
		title: 'Daily observation logged — night movement flagged by Carer Tom',
		meta: 'Today 02:18',
		color: teal,
	},
	{
		title: 'Care plan generated — October 2024',
		meta: '28 Oct 2024 · AI-generated, reviewed and approved by M. Johnson',
		color: '#9aa7a7',
	},
	{
		title: 'Monthly summary saved to profile',
		meta: '30 Sep 2024',
		color: '#9aa7a7',
	},
	{
		title: 'Resident onboarded via Care Vision API',
		meta: '14 Jun 2022 · Profile created, initial assessment uploaded',
		color: '#9aa7a7',
	},
];

const Tag = ({ label, color = 'gray' }) => (
	<Chip
		label={label}
		size="small"
		sx={{
			...tagStyles[color],
			borderRadius: '999px',
			fontWeight: 500,
			fontSize: 11,
			height: 24,
		}}
	/>
);

const MetricCard = ({ label, value, sub, valueColor, subColor }) => (
	<Paper
		elevation={0}
		sx={{
			p: 2,
			borderRadius: 2,
			bgcolor: surface,
		}}>
		<Typography fontSize={11} color={colors.muted} mb={0.5}>
			{label}
		</Typography>

		<Typography fontWeight={700} fontSize={22} color={valueColor || dark}>
			{value}
		</Typography>

		<Typography fontSize={11} color={subColor || colors.muted}>
			{sub}
		</Typography>
	</Paper>
);

const InfoRow = ({ label, value }) => (
	<Box
		sx={{
			py: 1,
			borderBottom: `1px solid ${border}`,
		}}>
		<Typography fontSize={11} color={colors.muted} fontWeight={500}>
			{label}
		</Typography>

		<Typography fontSize={13} fontWeight={500} color={colors.text}>
			{value}
		</Typography>
	</Box>
);

const cardStyle = {
	border: `1px solid ${border}`,
	borderRadius: 3,
	p: 2.5,
	boxShadow: 'none',
};

const tags = [
	{ label: 'API Onboarded', color: 'blue' },
	{ label: 'Plan Overdue 14d', color: 'red' },
	{ label: 'High Fall Risk', color: 'amber' },
	{ label: 'Dementia Stage 2', color: 'purple' },
	{ label: 'Hypertension', color: 'gray' },
	{ label: 'Osteoporosis', color: 'gray' },
];

const COLORS = ['red', 'amber', 'purple', 'gray'];

export default function ResidentProfile() {
	const navigate = useNavigate();
	const { state } = useLocation();
	const { residentId: residentIdFromUrl } = useParams();
	const homeId = getElderHomeId();
	const token = getAccessToken();
	const [tab, setTab] = useState(0);
	const [profile, setProfile] = useState({});
	const [rawProfileData, setRawProfileData] = useState({});
	const [plan, setPlan] = useState([]);
	const [observation, setObservation] = useState({});
	const [documents, setDocuments] = useState({});
	const [history, setHistory] = useState({});
	const [residentTags, setResidentTags] = useState([]);
	const [profileResId, setProfileResId] = useState('');
	const [isResidentExternal, setIsResidentExternal] = useState(false);
	const [currentPlanDate, setCurrentPlanDate] = useState('');
	const [residentCareNotes, setResidentCareNotes] = useState([]);
	const [openDelete, setOpenDelete] = useState(false);
	const [residentInfo, setResidentInfo] = useState('Resident Information');
	const [initialPlanNotAvailable, setIsInitialPlanNotAvailable] =
		useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isCurrentPlanOptimized, setIsCurrentPlanOptimized] = useState(false);
	const normalize = str => str?.toLowerCase().replace(/\s+/g, '').trim();

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const queryElderId = params.get('elderId');
		const queryExternal = params.get('external') === '1';

		const isExternal = state?.isExternalElder
			? state.isExternalElder
			: queryExternal;
		const profileId = state?.id || residentIdFromUrl;
		const elderId = state?.elderId || queryElderId || profileId;

		if (!profileId) {
			return;
		}

		setIsResidentExternal(isExternal);
		setProfileResId(profileId);
		onGetResident(profileId, isExternal);
		onGetCurrentPlan(isExternal ? elderId : profileId, isExternal);
		onGetResidentNotes(elderId, profileId, isExternal);
	}, []);

	async function onGetResident(elderId, isExternalElder = false) {
		setIsLoading(true);
		let reData;
		if (isExternalElder) {
			reData = await fetchExternalElder(elderId, token);
		} else {
			reData = await fetchElder(elderId, token);
			const reFiles = await fetchElderFiles(elderId, token);
			setDocuments(reFiles);
		}

		const fetchedTags = [
			isExternalElder ? 'API Onboarded' : 'Manually Onboarded',
			...(reData?.tags ?? []),
		];

		const tagsWithColors = fetchedTags.map((label, index) => ({
			label,
			color:
				index === 0
					? 'blue'
					: COLORS[Math.floor(Math.random() * COLORS.length)],
		}));

		setResidentTags(tagsWithColors);
		const info = getResidentInfo(reData);
		setResidentInfo(info);

		const {
			id,
			elderHomeId,
			createdDate,
			adminId,
			fileKey,
			tags,
			reviewed,
			...rest
		} = reData;

		const filteredProfiles = Object.fromEntries(
			Object.entries(rest).filter(([_, value]) => {
				return (
					value != null &&
					value !== '' &&
					value !== 0 &&
					!(Array.isArray(value) && value.length === 0)
				);
			}),
		);

		const flattenedProfiles = flattenObject(filteredProfiles);
		setProfile(flattenedProfiles);
	}

	function flattenObject(obj, parentKey = '') {
		const flattened = {};

		for (const [key, value] of Object.entries(obj)) {
			const newKey = parentKey ? `${parentKey}.${key}` : key;

			if (value && typeof value === 'object' && !Array.isArray(value)) {
				Object.assign(flattened, flattenObject(value, newKey));
			} else if (Array.isArray(value)) {
				flattened[newKey] = value.join(', ');
			} else {
				flattened[newKey] = value;
			}
		}

		return flattened;
	}

	async function onGetCurrentPlan(elderId = null, isExternalElder = false) {
		let response;
		let optimizedPlanIsEx = false;
		if (isExternalElder) {
			const planDetails = await fetchExternalLatestElderPlanData(
				homeId,
				elderId,
				token,
			);

			if (
				!planDetails.id ||
				planDetails.id === '' ||
				planDetails.planType === 'NONE'
			) {
				setPlan([]);
				setIsInitialPlanNotAvailable(true);
				return;
			}

			optimizedPlanIsEx = planDetails.isPlanExternal;

			if (planDetails.isPlanExternal) {
				response = await fetchExternalElderPlan(planDetails.id, token);
				setIsCurrentPlanOptimized(false);
			} else {
				response = await fetchCarePlanById(
					planDetails.id,
					token,
					'optimized',
				);
				setIsCurrentPlanOptimized(true);
			}
		} else {
			const manualPlanDetails = await fetchLatestElderPlanData(
				elderId,
				token,
			);

			if (
				!manualPlanDetails.id ||
				manualPlanDetails.id === '' ||
				manualPlanDetails.planType === 'NONE'
			) {
				setPlan([]);
				setIsInitialPlanNotAvailable(true);
				return;
			}

			const isOptimized = manualPlanDetails.planType === 'optimized';

			response = await fetchCarePlanById(
				manualPlanDetails.id,
				token,
				isOptimized ? 'optimized' : 'initial',
			);
			setIsCurrentPlanOptimized(isOptimized);
		}

		if (!response.error) {
			const date = new Date(response.createdDate);
			setCurrentPlanDate(
				isNaN(date.getTime())
					? 'Not Available'
					: date.toLocaleDateString('en-US', {
							month: 'long',
							year: 'numeric',
						}),
			);
			const planData = cleanCarePlan(
				optimizedPlanIsEx ? response.carePlan.plan : response.plan,
			);
			setPlan(planData);
		} else {
			toast.error(response.message);
		}
	}

	async function onGetResidentNotes(
		elderId,
		profileId,
		isExternalElder = false,
	) {
		let notes = [];

		if (isExternalElder) {
			const response = await fetchExternalElderReview(elderId, token);

			notes = (response || []).map(item => ({
				name: 'External Review',
				date: formatDate(item.createdDate),
				note: item.review,
			}));
		}

		const response = await fetchElderCareNotes(
			`note/elder/${profileId}`,
			token,
		);

		notes = (response || []).map(item => ({
			name: item.generatedUserName,
			date: formatDate(item.createdDate),
			note: item.enhancedNote,
		}));

		setResidentCareNotes(notes);
		setIsLoading(false);
	}

	const formatDate = dateString => {
		if (!dateString) return '-';

		const date = new Date(dateString.replace(' ', 'T'));

		const now = new Date();

		const isToday =
			date.getDate() === now.getDate() &&
			date.getMonth() === now.getMonth() &&
			date.getFullYear() === now.getFullYear();

		if (isToday) {
			return `Today ${date.toLocaleTimeString([], {
				hour: '2-digit',
				minute: '2-digit',
			})}`;
		}

		return date.toLocaleDateString();
	};

	const getResidentInfo = resident => {
		const formattedDob = new Date(resident.dateOfBirth).toLocaleDateString(
			'en-GB',
			{
				day: 'numeric',
				month: 'long',
				year: 'numeric',
			},
		);

		const formattedAdmittedDate = new Date(
			resident.createdDate,
		).toLocaleDateString('en-GB', {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		});

		return `· DOB: ${formattedDob} ${resident.age > 0 ? `· Age (${resident.age})` : ''} · Admitted: ${formattedAdmittedDate}`;
	};

	async function removeResident() {
		const response = await deleteResident(profileResId, token);
		if (response.message === 'Success') {
			toast.success('Resident removed Successfully');
			const previousRoute = state?.from;
			navigate(previousRoute);
		} else {
			toast.success(response.message);
		}
	}

	function cleanCarePlan(plan) {
		if (!Array.isArray(plan)) return [];

		const sectionOrder = [
			'Current Situation',
			'Goals',
			'Action Description',
		];

		return plan
			.filter(item => item?.bodyContent?.length > 0)
			.map(item => {
				const orderedBodyContent = [
					...sectionOrder
						.map(key =>
							item.bodyContent.find(
								sub => normalize(sub.label) === normalize(key),
							),
						)
						.filter(Boolean),

					...item.bodyContent.filter(
						sub =>
							!sectionOrder.some(
								key => normalize(sub.label) === normalize(key),
							),
					),
				];

				return {
					title: item?.title,
					bodyContent: orderedBodyContent,
				};
			});
	}

	function getPlanItemText(item) {
		if (item == null) return '';

		if (typeof item === 'string' || typeof item === 'number') {
			return `${item}`;
		}

		if (typeof item === 'object') {
			return item.text ?? item.label ?? item.content ?? item.title ?? '';
		}

		return '';
	}

	async function onDownloadResidentFile(key, type, isForDownload = false) {
		let routePath = '';

		if (type === FILE_ITEM_FILTER[2]) {
			routePath = `plan/initial/file?key=${key}&download=${isForDownload}`;
		} else if (type === FILE_ITEM_FILTER[3]) {
			routePath = `plan/optimized/file?key=${key}&download=${isForDownload}`;
		} else if (type === FILE_ITEM_FILTER[1]) {
			routePath = `elder/file?key=${key}&download=${isForDownload}`;
		} else {
			return;
		}

		const response = await fetchElderDataFile(routePath, token);
		if (!response.error) {
			if (isForDownload) {
				const link = document.createElement('a');
				link.href = response;
				link.setAttribute('download', '');
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				window.open(response, '_blank');
			}
		} else {
			toast.error(response.message);
		}
	}

	function onRenderSections(mainIndex, groupIndex, itemIndex, item) {
		const itemText = getPlanItemText(item);

		return (
			<Stack
				key={itemIndex}
				direction="row"
				spacing={1.5}
				alignItems="flex-start">
				<Check
					sx={{
						color: teal,
						fontSize: 20,
						mt: '2px',
						flexShrink: 0,
					}}
				/>
				<Typography
					variant="body2"
					sx={{
						lineHeight: 1.7,
						color: '#334155',
						fontSize: '13px',
					}}>
					{itemText}
				</Typography>
			</Stack>
		);
	}

	return (
		<MainGrid>
			<Box
				sx={{
					width: '100%',
				}}>
				{/* Back */}
				<Box
					onClick={() => {
						const previousRoute = state?.from;
						navigate(previousRoute);
					}}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 0.5,
						mb: 1.5,
						color: colors.muted,
						cursor: 'pointer',
						fontSize: 13,
					}}>
					<ChevronLeftIcon sx={{ fontSize: 18 }} />
					Back to Dashboard
				</Box>

				{/* Alert */}
				{/* <Alert
					severity="error"
					icon={<WarningAmberIcon />}
					sx={{
						mb: 2,
						borderRadius: 2,
						bgcolor: colors.dangerBg,
						border: '1px solid #f09595',
						color: colors.danger,
					}}>
					<Typography fontSize={13}>
						<b>Care plan overdue — 14 days.</b> Last plan: October
						2024. New fall risk pattern detected via Arquella. Night
						movement increased 40% over 7 days. Immediate review
						recommended.
					</Typography>
				</Alert> */}

				{/* Profile Header */}
				<Paper sx={cardStyle}>
					<Stack
						direction={{ xs: 'column', lg: 'row' }}
						spacing={3}
						alignItems={{ lg: 'center' }}>
						<Avatar
							sx={{
								width: 60,
								height: 60,
								bgcolor: '#9a2e2e',
								fontWeight: 700,
								fontSize: 20,
							}}>
							{profile?.name
								?.trim()
								?.split(' ')
								?.filter(Boolean)
								?.map(word => word[0])
								?.join('')
								?.toUpperCase()
								?.slice(0, 2) ?? ''}
						</Avatar>

						{isLoading || Object.keys(profile).length === 0 ? (
							<Box flex={1}>
								<Skeleton
									variant="rectangular"
									height={30}
									sx={{
										borderRadius: '10px',
										width: 250,
										mb: 1,
									}}
								/>
								<Skeleton
									variant="rectangular"
									height={15}
									sx={{
										borderRadius: '10px',
										width: 300,
										mb: 1,
									}}
								/>
								<Skeleton
									variant="rectangular"
									height={20}
									sx={{
										borderRadius: '10px',
										width: 150,
									}}
								/>
							</Box>
						) : (
							<Box flex={1}>
								<Typography
									fontWeight={700}
									fontSize={24}
									color={dark}>
									{profile.name}
								</Typography>

								<Typography
									mt={0.5}
									fontSize={13}
									color={colors.muted}>
									{residentInfo}
								</Typography>

								<Stack
									direction="row"
									spacing={1}
									mt={1.5}
									flexWrap="wrap"
									useFlexGap>
									{residentTags.map((tag, index) => (
										<Tag
											key={index}
											label={tag.label}
											color={tag.color}
										/>
									))}
								</Stack>
							</Box>
						)}

						{!isLoading && (
							<Stack direction="row" spacing={1} flexWrap="wrap">
								{
									<Button
										onClick={() => {
											if (initialPlanNotAvailable) {
												navigate(`/initialCarePlan`);
											} else {
												navigate(
													`/carePlan/${profileResId}`,
													{
														state: {
															id: profileResId,
														},
													},
												);
											}
										}}
										variant="contained"
										startIcon={<SyncIcon />}
										sx={{
											bgcolor: teal,
											color: dark,
											borderRadius: 2,
											px: 2,
											boxShadow: 'none',
											fontWeight: 600,
											'&:hover': {
												bgcolor: '#00b0a0',
												boxShadow: 'none',
											},
											fontSize: 13,
											textTransform: 'none',
										}}>
										{initialPlanNotAvailable
											? 'Create Plan'
											: 'Optimize Plan'}
									</Button>
								}

								<Button
									onClick={() =>
										navigate('/summarizer', {
											state: { id: '' },
										})
									}
									variant="outlined"
									startIcon={<DescriptionIcon />}
									sx={{
										borderColor: border,
										color: colors.text,
										borderRadius: 2,
										fontSize: 13,
										textTransform: 'none',
									}}>
									Summarize
								</Button>

								{!isResidentExternal && (
									<Button
										onClick={() => setOpenDelete(true)}
										variant="outlined"
										startIcon={<Delete />}
										sx={{
											borderColor: 'error.main',
											color: 'error.main',
											borderRadius: 2,
											fontSize: 13,
											textTransform: 'none',
											'&:hover': {
												backgroundColor: 'error.light',
												borderColor: 'error.main',
												color: 'white',
											},
										}}>
										Remove Profile
									</Button>
								)}

								<Dialog
									open={openDelete}
									onClose={() => setOpenDelete(false)}>
									<DialogTitle style={DialogTitleStyle}>
										Remove Profile
									</DialogTitle>

									<DialogContent
										variant="subtitle1"
										style={{
											color: 'rgba(113, 113, 113, 1)',
											fontWeight: 600,
										}}>
										<Typography sx={{ fontSize: 14 }}>
											Are you sure you want to remove this
											profile? This action cannot be
											undone.
										</Typography>
									</DialogContent>

									<DialogActions
										style={{
											marginRight: '20px',
											marginBottom: '10px',
										}}>
										<Button
											onClick={() => setOpenDelete(false)}
											sx={{
												paddingLeft: '15px',
												paddingRight: '5px',
												color: '#28696D',
												textTransform: 'none',
												marginRight: '0px',
											}}>
											Cancel
										</Button>

										<Button
											variant="contained"
											sx={{
												backgroundColor: teal,
												boxShadow: 'none',
												marginLeft: '10px',
												textTransform: 'none',
												'&:hover': {
													backgroundColor: teal,
													boxShadow: 'none',
												},
											}}
											onClick={() => {
												setOpenDelete(false);
												removeResident();
											}}>
											Delete
										</Button>
									</DialogActions>
								</Dialog>

								{/* <Button
								startIcon={<DownloadForOfflineIcon />}
								sx={{
									borderColor: border,
									color: colors.text,
									borderRadius: 2,
									textTransform: 'none',
									fontSize: 13,
								}}>
								Export
							</Button> */}
							</Stack>
						)}
					</Stack>
				</Paper>

				{/* Tabs */}
				<Box sx={{ mt: 2 }}>
					<Tabs
						value={tab}
						onChange={(e, v) => setTab(v)}
						textColor="inherit"
						TabIndicatorProps={{
							style: {
								backgroundColor: teal,
								height: 3,
							},
						}}
						sx={{
							borderBottom: `1px solid ${border}`,
							mb: 3,
							'& .MuiTab-root': {
								textTransform: 'none',
								fontWeight: 500,
								fontSize: 13,
								color: colors.muted,
								minHeight: 46,
							},
							'& .Mui-selected': {
								color: dark,
							},
						}}>
						<Tab label="Overview" />
						<Tab label="Care Plans" />
						<Tab label="Observations" />
						{!isResidentExternal && <Tab label="Documents" />}
						{/* <Tab label="History" /> */}
					</Tabs>

					{/* OVERVIEW */}
					{tab === 0 && (
						<Stack spacing={2}>
							<Grid container>
								{/* Personal Details */}
								<Grid item xs={12} lg={12}>
									<Paper sx={cardStyle}>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
											mb={2}>
											<PersonIcon
												sx={{
													color: teal,
													fontSize: 18,
												}}
											/>
											<Typography
												fontWeight={600}
												fontSize={14}>
												Personal Details
											</Typography>
										</Stack>

										<Grid container spacing={2}>
											{isLoading ||
											Object.entries(profile).length ===
												0 ? (
												<Grid
													container
													flexDirection={'column'}
													padding={'10px'}
													marginLeft={'5px'}>
													<Skeleton
														variant="rectangular"
														height={50}
														sx={{
															borderRadius:
																'10px',
														}}
													/>
												</Grid>
											) : (
												Object.entries(profile)
													.filter(([key, value]) =>
														isResidentExternal
															? value
															: key.startsWith(
																	'elderHistory.',
																) && value,
													)
													.map(([key, value]) => {
														const label = key
															.replace(
																'elderHistory.',
																'',
															)
															.replace(
																/([A-Z])/g,
																' $1',
															)
															.replace(
																/^./,
																str =>
																	str.toUpperCase(),
															);

														return (
															<Grid
																item
																xs={6}
																key={key}>
																<InfoRow
																	label={
																		label
																	}
																	value={
																		value
																	}
																/>
															</Grid>
														);
													})
											)}
										</Grid>
									</Paper>
								</Grid>

								{/* AI Insight */}
								{/* <Grid item xs={12} lg={6}>
									<Paper sx={cardStyle}>
										<Stack
											direction="row"
											alignItems="center"
											spacing={1}
											mb={2}
											flexWrap="wrap">
											<FavoriteIcon
												sx={{
													color: teal,
													fontSize: 18,
												}}
											/>

											<Typography
												fontWeight={600}
												fontSize={14}>
												AI Health Insight
											</Typography>

											<Chip
												icon={
													<AutoAwesomeIcon
														sx={{
															fontSize:
																'14px !important',
														}}
													/>
												}
												label="Generated now"
												size="small"
												sx={{
													bgcolor: dark,
													color: teal,
													height: 24,
													borderRadius: '999px',
													fontSize: 11,
												}}
											/>
										</Stack>

										<Box
											sx={{
												p: 1.5,
												borderRadius: 2,
												bgcolor: surface,
												mb: 2,
											}}>
											<Typography
												fontSize={13}
												lineHeight={1.7}
												color={colors.muted}>
												Maggie shows increased night
												movement (+40% over 7 days) and
												two near-fall events logged by
												staff. Low mood noted in 4
												observations this week —
												activity participation dropped
												from 70% to 30%.
											</Typography>
										</Box>

										<Grid container spacing={1}>
											<Grid item xs={6}>
												<MetricCard
													label="Fall Risk"
													value="High"
													sub="↑ since October"
													valueColor={colors.danger}
													subColor={colors.danger}
												/>
											</Grid>

											<Grid item xs={6}>
												<MetricCard
													label="Wellbeing Score"
													value="3.2/5"
													sub="↓ from 4.1"
													subColor={colors.warning}
												/>
											</Grid>

											<Grid item xs={6}>
												<MetricCard
													label="Obs. this week"
													value="12"
													sub="by 4 staff"
													subColor="#2d7a4a"
												/>
											</Grid>

											<Grid item xs={6}>
												<MetricCard
													label="Plan Age"
													value="14 days"
													sub="Overdue"
													valueColor={colors.danger}
													subColor={colors.danger}
												/>
											</Grid>
										</Grid>
									</Paper>
								</Grid> */}
							</Grid>

							{/* Conditions */}
							{/* <Paper sx={cardStyle}>
								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
									mb={2}>
									<MedicalServicesIcon
										sx={{
											color: teal,
											fontSize: 18,
										}}
									/>

									<Typography fontWeight={600} fontSize={14}>
										Conditions & Medications
									</Typography>
								</Stack>

								<Grid container spacing={4}>
									<Grid item xs={12} md={6}>
										<Typography
											fontSize={11}
											fontWeight={700}
											color={colors.muted}
											textTransform="uppercase"
											mb={2}>
											Active Conditions
										</Typography>

										<Stack spacing={1}>
											<Typography fontSize={13}>
												🔴 Dementia — Moderate Stage 2
											</Typography>

											<Typography fontSize={13}>
												🟠 Hypertension (managed)
											</Typography>

											<Typography fontSize={13}>
												🟠 Osteoporosis
											</Typography>

											<Typography fontSize={13}>
												🟢 Type 2 Diabetes (controlled)
											</Typography>
										</Stack>
									</Grid>

									<Grid item xs={12} md={6}>
										<Typography
											fontSize={11}
											fontWeight={700}
											color={colors.muted}
											textTransform="uppercase"
											mb={2}>
											Current Medications
										</Typography>

										<Stack spacing={1}>
											<Typography fontSize={13}>
												Donepezil 10mg — once daily
											</Typography>

											<Typography fontSize={13}>
												Amlodipine 5mg — once daily
											</Typography>

											<Typography fontSize={13}>
												Calcium + Vit D — once daily
											</Typography>

											<Typography fontSize={13}>
												Metformin 500mg — twice daily
											</Typography>
										</Stack>
									</Grid>
								</Grid>
							</Paper> */}
						</Stack>
					)}

					{/* CARE PLANS */}
					{tab === 1 && (
						<Paper sx={cardStyle}>
							<Stack
								direction="row"
								justifyContent="space-between"
								mb={2}
								flexWrap="wrap">
								<Typography fontWeight={600}>
									{currentPlanDate &&
										currentPlanDate !== 'Not available' && (
											<>
												Latest Care Plan -{' '}
												{currentPlanDate}
											</>
										)}
								</Typography>

								{isCurrentPlanOptimized && (
									<Chip
										icon={
											<AutoAwesome
												style={{
													fontSize: 14,
													color: teal,
												}}
											/>
										}
										label={`Optimized Plan`}
										size="small"
										sx={{
											height: 24,
											bgcolor: dark,
											color: teal,
											fontSize: 11,
											fontWeight: 600,
										}}
									/>
								)}
							</Stack>

							{/* Section */}
							<Stack spacing={2.5}>
								{plan.length > 0 ? (
									plan.map((section, index) => (
										<Card
											key={section.title}
											sx={{
												borderRadius: 3,
												border: '1px solid #e2e8f0',
												boxShadow: 'none',
											}}>
											<CardContent sx={{ p: 0 }}>
												{/* Header */}
												<Box
													sx={{
														px: 3,
														py: '5px',
														display: 'flex',
														justifyContent:
															'space-between',
														alignItems: 'center',
														backgroundColor:
															'#e6f9f7',
													}}>
													<Stack
														direction="row"
														spacing={1.5}
														alignItems="center"
														sx={{
															backgroundColor:
																'#e6f9f7',
														}}>
														<Box
															sx={{
																width: 36,
																height: 36,
																borderRadius: 2,
																display: 'flex',
																alignItems:
																	'center',
																justifyContent:
																	'center',
																...sectionIconBg,
															}}>
															<Description fontSize="small" />
														</Box>

														<Typography
															fontWeight={700}
															fontSize={'13px'}>
															{section.title}
														</Typography>
													</Stack>
												</Box>

												<Divider />

												{/* Body */}
												<Box sx={{ px: 3, py: 2 }}>
													<Stack spacing={3}>
														{section.bodyContent.map(
															(
																group,
																groupIndex,
															) => (
																<Box
																	key={
																		group.label
																	}>
																	{/* Label */}
																	<Typography
																		variant="subtitle2"
																		sx={{
																			fontWeight: 700,
																			color: '#0f172a',
																			mb: 1.5,
																			fontSize:
																				'13px',
																		}}>
																		{
																			group.label
																		}
																	</Typography>

																	{/* Items */}
																	<Stack
																		spacing={
																			1.5
																		}>
																		{group.content.map(
																			(
																				item,
																				itemIndex,
																			) => {
																				return onRenderSections(
																					index,
																					groupIndex,
																					itemIndex,
																					item,
																				);
																			},
																		)}
																	</Stack>
																</Box>
															),
														)}
													</Stack>
												</Box>
											</CardContent>
										</Card>
									))
								) : (
									<Card
										sx={{
											borderRadius: '18px',
											border: '1px solid #d8e8e8',
											boxShadow: 'none',
											alignItems: 'center',
										}}>
										<CardContent
											sx={{
												p: 3,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												minHeight: 100,
												color: '#6b8888',
												fontSize: 13,
												fontWeight: 500,
											}}>
											No Care Plan for the resident
										</CardContent>
									</Card>
								)}
							</Stack>
						</Paper>
					)}

					{/* OBSERVATIONS */}
					{tab === 2 && (
						<Paper sx={cardStyle}>
							<Stack
								direction="row"
								justifyContent="space-between"
								mb={2}
								flexWrap="wrap">
								<Stack direction="row" spacing={1}>
									<NotesIcon
										sx={{
											color: teal,
											fontSize: 20,
										}}
									/>
									<Typography fontWeight={600} fontSize={13}>
										Daily Observations
									</Typography>
								</Stack>

								<Tag label="Staff Mobile App" color="blue" />
							</Stack>

							<TableContainer>
								<Table sx={{ tableLayout: 'fixed' }}>
									<TableHead>
										<TableRow
											sx={{
												bgcolor: surface,
											}}>
											<TableCell sx={{ fontSize: 13 }}>
												Date / Time
											</TableCell>
											<TableCell sx={{ fontSize: 13 }}>
												Staff
											</TableCell>
											<TableCell
												sx={{
													width: '55%',
													fontSize: 13,
												}}>
												Observation
											</TableCell>
											{/* <TableCell>Category</TableCell> */}
											<TableCell
												sx={{
													width: '10%',
													fontSize: 13,
												}}>
												Flagged
											</TableCell>
										</TableRow>
									</TableHead>

									<TableBody>
										{residentCareNotes.length > 0 ? (
											residentCareNotes.map(
												(note, index) => (
													<TableRow key={index} hover>
														<TableCell
															sx={{
																fontSize: 13,
															}}>
															{note.date}
														</TableCell>
														<TableCell
															sx={{
																fontSize: 13,
															}}>
															{note.name}
														</TableCell>
														<TableCell
															sx={{
																fontSize: 13,
															}}>
															{note.note}
														</TableCell>
														{/* <TableCell>
															<Tag
																label="Wellbeing"
																color="amber"
															/>
														</TableCell> */}
														<TableCell>
															<Tag
																label="Yes"
																color="amber"
															/>
														</TableCell>
													</TableRow>
												),
											)
										) : (
											<TableRow>
												<TableCell
													colSpan={4}
													align="center">
													<Typography variant="tableBody">
														No data available
													</Typography>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>
					)}

					{/* DOCUMENTS */}
					{tab === 3 && (
						<Paper sx={cardStyle}>
							<Stack direction="row" spacing={1} mb={2}>
								<FolderIcon sx={{ color: teal }} />

								<Typography fontWeight={600}>
									Documents & Reports
								</Typography>
							</Stack>

							<TableContainer>
								<Table>
									<TableHead>
										<TableRow sx={{ bgcolor: surface }}>
											<TableCell>Document</TableCell>
											<TableCell>Type</TableCell>
											<TableCell>Generated</TableCell>
											<TableCell>By</TableCell>
											<TableCell>Actions</TableCell>
										</TableRow>
									</TableHead>

									<TableBody>
										{documents.length > 0 &&
											documents.map((document, index) => (
												<TableRow key={index} hover>
													<TableCell>
														{
															document.key.split(
																'_',
															)[1]
														}
													</TableCell>

													<TableCell>
														<Tag
															label={
																document.type
															}
															color="teal"
														/>
													</TableCell>

													<TableCell>
														{dateConverter(
															document.date,
														)}
													</TableCell>

													<TableCell>N/A</TableCell>

													<TableCell>
														<Stack
															direction="row"
															spacing={1}>
															<IconButton
																size="small"
																onClick={() => {
																	onDownloadResidentFile(
																		document.key,
																		document.type,
																		true,
																	);
																}}
																sx={{
																	width: 34,
																	height: 28,
																	border: '1px solid #d8e8e8',
																	borderRadius:
																		'8px',
																	backgroundColor:
																		'#fff',
																	color: '#1a2424',
																	'&:hover': {
																		backgroundColor:
																			surface,
																		borderColor:
																			teal,
																		color: dark,
																	},
																}}>
																<DownloadForOfflineIcon
																	fontSize="small"
																	style={{
																		color: teal,
																	}}
																/>
															</IconButton>
														</Stack>
													</TableCell>
												</TableRow>
											))}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>
					)}

					{/* HISTORY */}
					{tab === 4 && (
						<Paper
							elevation={0}
							sx={{
								border: `1px solid ${border}`,
								borderRadius: '12px',
								p: 3,
								background: '#fff',
							}}>
							{/* Header */}
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
								mb={3}>
								<TimelineIcon
									sx={{
										color: teal,
										fontSize: 18,
									}}
								/>

								<Typography
									sx={{
										fontSize: 14,
										fontWeight: 600,
										color: colors.text,
									}}>
									Resident History
								</Typography>
							</Stack>

							{/* Timeline */}
							<Box
								sx={{
									position: 'relative',
									pl: '22px',

									'&::before': {
										content: '""',
										position: 'absolute',
										left: '7px',
										top: '6px',
										bottom: '6px',
										width: '1px',
										background: border,
									},
								}}>
								{timelineData.map((item, index) => (
									<Box
										key={index}
										sx={{
											position: 'relative',
											mb: 3,
										}}>
										{/* Dot */}
										<Box
											sx={{
												position: 'absolute',
												left: '-19px',
												top: '4px',
												width: '12px',
												height: '12px',
												borderRadius: '50%',
												background: item.color,
												border: `2px solid ${colors.bg}`,
												zIndex: 2,
											}}
										/>

										{/* Title */}
										<Typography
											sx={{
												fontSize: 13,
												fontWeight: 500,
												color: colors.text,
												lineHeight: 1.4,
											}}>
											{item.title}
										</Typography>

										{/* Meta */}
										<Typography
											sx={{
												fontSize: 11,
												color: colors.muted,
												mt: 0.5,
											}}>
											{item.meta}
										</Typography>

										{/* Body */}
										{item.body && (
											<Typography
												sx={{
													fontSize: 12,
													color: colors.muted,
													mt: 1,
													lineHeight: 1.6,
												}}>
												{item.body}
											</Typography>
										)}
									</Box>
								))}
							</Box>
						</Paper>
					)}
				</Box>
			</Box>
		</MainGrid>
	);
}
